import React from "react";
import { useSelector } from "react-redux";
import { Badge, Button, Col, Container, Form, Modal, Row } from "react-bootstrap";
import Swal from "sweetalert2";
import {
  getAdminMemberTerms,
  updateAdminMemberTerms,
} from "stores";
import {
  memberTermsHighlights,
  memberTermsIntro,
  memberTermsSections,
} from "member/memberTerms";
import "./admin-member-terms.css";

const ALLOWED_ROLES = ["super admin", "admin"];
const ICON_OPTIONS = [
  { value: "fas fa-info-circle", label: "Informasi" },
  { value: "fas fa-user-plus", label: "Pendaftaran" },
  { value: "fas fa-money-check-alt", label: "Pencairan" },
  { value: "fas fa-receipt", label: "Pelunasan" },
  { value: "fas fa-hand-holding-usd", label: "Pinjaman" },
  { value: "fas fa-file-invoice-dollar", label: "Dokumen keuangan" },
  { value: "fas fa-wallet", label: "Simpanan" },
  { value: "fas fa-door-open", label: "Keluar kepesertaan" },
];

function defaultTerms() {
  return JSON.parse(
    JSON.stringify({
      intro: memberTermsIntro,
      highlights: memberTermsHighlights,
      sections: memberTermsSections,
    }),
  );
}

function cloneTerms(value) {
  return JSON.parse(JSON.stringify(value));
}

function bodyToText(body) {
  return Array.isArray(body) ? body.join("\n") : String(body || "");
}

function textToBody(value) {
  const lines = String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  return lines.length > 1 ? lines : lines[0] || "";
}

function emptyEditor(type) {
  return {
    show: false,
    type,
    index: null,
    sectionIndex: null,
    itemIndex: null,
    title: "",
    body: "",
    icon: "fas fa-info-circle",
    links: [],
  };
}

function formatUpdatedAt(value) {
  if (!value) return "Belum pernah disimpan dari halaman admin";
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function getErrorMessage(error, fallback) {
  return error?.message || error?.data?.message || fallback;
}

export default function AdminMemberTerms() {
  const auth = useSelector((state) => state.authReducer);
  const [terms, setTerms] = React.useState(defaultTerms);
  const [metadata, setMetadata] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [editor, setEditor] = React.useState(emptyEditor(""));

  const canEdit = ALLOWED_ROLES.includes(auth.role);

  const loadTerms = React.useCallback(async () => {
    if (!canEdit) return;

    setLoading(true);
    try {
      const response = await getAdminMemberTerms();
      if (response.data) {
        setTerms({
          intro: response.data.intro,
          highlights: response.data.highlights || [],
          sections: response.data.sections || [],
        });
        setMetadata(response.data);
      } else {
        setTerms(defaultTerms());
        setMetadata(null);
      }
    } catch (error) {
      Swal.fire(
        "Gagal memuat ketentuan",
        getErrorMessage(error, "Silakan coba kembali."),
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [canEdit]);

  React.useEffect(() => {
    loadTerms();
  }, [loadTerms]);

  async function persist(nextTerms, successMessage) {
    setSaving(true);
    try {
      const response = await updateAdminMemberTerms(nextTerms);
      setTerms({
        intro: response.data.intro,
        highlights: response.data.highlights || [],
        sections: response.data.sections || [],
      });
      setMetadata(response.data);
      await Swal.fire({
        icon: "success",
        title: "Berhasil",
        text: successMessage,
        timer: 1600,
        showConfirmButton: false,
      });
      return true;
    } catch (error) {
      await Swal.fire(
        "Gagal menyimpan",
        getErrorMessage(error, "Perubahan ketentuan belum tersimpan."),
        "error",
      );
      return false;
    } finally {
      setSaving(false);
    }
  }

  function openIntroEditor() {
    setEditor({
      ...emptyEditor("intro"),
      show: true,
      title: terms.intro.title,
      body: terms.intro.body,
    });
  }

  function openHighlightEditor(index = null) {
    const item = index === null ? null : terms.highlights[index];
    setEditor({
      ...emptyEditor("highlight"),
      show: true,
      index,
      title: item?.title || "",
      body: item?.body || "",
      icon: item?.icon || "fas fa-info-circle",
    });
  }

  function openSectionEditor(sectionIndex = null) {
    const section =
      sectionIndex === null ? null : terms.sections[sectionIndex];
    setEditor({
      ...emptyEditor("section"),
      show: true,
      sectionIndex,
      title: section?.title || "",
      icon: section?.icon || "fas fa-info-circle",
    });
  }

  function openItemEditor(sectionIndex, itemIndex = null) {
    const item =
      itemIndex === null ? null : terms.sections[sectionIndex].items[itemIndex];
    setEditor({
      ...emptyEditor("item"),
      show: true,
      sectionIndex,
      itemIndex,
      title: item?.title || "",
      body: bodyToText(item?.body),
      links: cloneTerms(item?.links || []),
    });
  }

  function closeEditor() {
    if (!saving) setEditor(emptyEditor(""));
  }

  function updateEditor(field, value) {
    setEditor((current) => ({ ...current, [field]: value }));
  }

  function updateLink(index, field, value) {
    setEditor((current) => ({
      ...current,
      links: current.links.map((link, linkIndex) =>
        linkIndex === index ? { ...link, [field]: value } : link,
      ),
    }));
  }

  function addLink() {
    setEditor((current) => ({
      ...current,
      links: [...current.links, { label: "", url: "" }],
    }));
  }

  function removeLink(index) {
    setEditor((current) => ({
      ...current,
      links: current.links.filter((_, linkIndex) => linkIndex !== index),
    }));
  }

  async function submitEditor(event) {
    event.preventDefault();

    const title = editor.title.trim();
    const body = editor.body.trim();
    if (!title || (editor.type !== "section" && !body)) {
      Swal.fire(
        "Data belum lengkap",
        "Judul dan isi ketentuan wajib diisi.",
        "warning",
      );
      return;
    }

    const hasIncompleteLink = editor.links.some(
      (link) => !link.label.trim() || !link.url.trim(),
    );
    const hasInvalidUrl = editor.links.some(
      (link) => !/^https?:\/\//i.test(link.url.trim()),
    );
    if (hasIncompleteLink || hasInvalidUrl) {
      Swal.fire(
        "Tautan belum valid",
        "Nama tautan dan URL lengkap yang diawali http:// atau https:// wajib diisi.",
        "warning",
      );
      return;
    }

    const confirmation = await Swal.fire({
      title: editor.index === null && editor.itemIndex === null &&
        editor.sectionIndex === null && editor.type !== "intro"
        ? "Tambahkan ketentuan?"
        : "Simpan perubahan?",
      text: "Perubahan akan langsung tampil pada halaman member.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Ya, simpan",
      cancelButtonText: "Batal",
      reverseButtons: true,
    });
    if (!confirmation.isConfirmed) return;

    const next = cloneTerms(terms);
    if (editor.type === "intro") {
      next.intro = { title, body };
    }

    if (editor.type === "highlight") {
      const value = { title, body, icon: editor.icon };
      if (editor.index === null) next.highlights.push(value);
      else next.highlights[editor.index] = {
        ...next.highlights[editor.index],
        ...value,
      };
    }

    if (editor.type === "section") {
      const value = { title, icon: editor.icon };
      if (editor.sectionIndex === null) {
        next.sections.push({ ...value, items: [] });
      } else {
        next.sections[editor.sectionIndex] = {
          ...next.sections[editor.sectionIndex],
          ...value,
        };
      }
    }

    if (editor.type === "item") {
      const value = {
        title,
        body: textToBody(body),
        links: editor.links.map((link) => ({
          label: link.label.trim(),
          url: link.url.trim(),
        })),
      };
      if (editor.itemIndex === null) {
        next.sections[editor.sectionIndex].items.push(value);
      } else {
        next.sections[editor.sectionIndex].items[editor.itemIndex] = {
          ...next.sections[editor.sectionIndex].items[editor.itemIndex],
          ...value,
        };
      }
    }

    const saved = await persist(next, "Ketentuan member sudah diperbarui.");
    if (saved) closeEditor();
  }

  async function deleteContent(type, sectionIndex, itemIndex) {
    const labels = {
      highlight: "ringkasan ketentuan",
      section: "kategori beserta seluruh isinya",
      item: "detail ketentuan",
    };
    const confirmation = await Swal.fire({
      title: `Hapus ${labels[type]}?`,
      text: "Data yang dihapus tidak akan tampil lagi pada halaman member.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus",
      cancelButtonText: "Batal",
      confirmButtonColor: "#dc3545",
      reverseButtons: true,
    });
    if (!confirmation.isConfirmed) return;

    const next = cloneTerms(terms);
    if (type === "highlight") next.highlights.splice(sectionIndex, 1);
    if (type === "section") next.sections.splice(sectionIndex, 1);
    if (type === "item") {
      next.sections[sectionIndex].items.splice(itemIndex, 1);
    }

    await persist(next, "Ketentuan berhasil dihapus.");
  }

  if (auth.role && !canEdit) {
    return (
      <Container fluid className="admin-terms-page">
        <div className="admin-terms-access">
          <i className="fas fa-lock" />
          <h3>Akses tidak tersedia</h3>
          <p>Menu ini hanya dapat dibuka oleh admin dan super admin.</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="admin-terms-page">
      <header className="admin-terms-header">
        <div>
          <span className="admin-terms-eyebrow">Konten member</span>
          <h2>Kelola Ketentuan PUGAR</h2>
          <p>Terakhir diperbarui: {formatUpdatedAt(metadata?.updatedAt)}</p>
        </div>
        <Button
          variant="outline-secondary"
          onClick={loadTerms}
          disabled={loading || saving}
          title="Muat ulang data"
        >
          <i className="fas fa-sync-alt" />
          Muat ulang
        </Button>
      </header>

      {loading ? (
        <div className="admin-terms-loading">
          <i className="fas fa-circle-notch fa-spin" />
          <span>Memuat ketentuan...</span>
        </div>
      ) : (
        <>
          <section className="admin-terms-intro">
            <div>
              <span>Pembuka halaman member</span>
              <h3>{terms.intro.title}</h3>
              <p>{terms.intro.body}</p>
            </div>
            <Button variant="primary" onClick={openIntroEditor}>
              <i className="fas fa-pen" />
              Edit
            </Button>
          </section>

          <section className="admin-terms-block">
            <div className="admin-terms-block-title">
              <div>
                <h3>Ringkasan Utama</h3>
                <span>{terms.highlights.length} ringkasan tampil di member</span>
              </div>
              <Button
                variant="success"
                onClick={() => openHighlightEditor()}
              >
                <i className="fas fa-plus" />
                Tambah
              </Button>
            </div>
            {terms.highlights.length === 0 ? (
              <div className="admin-terms-empty">Belum ada ringkasan.</div>
            ) : (
              <Row className="admin-terms-highlight-list">
                {terms.highlights.map((item, index) => (
                  <Col lg={4} md={6} key={item._id || `${item.title}-${index}`}>
                    <article className="admin-terms-highlight">
                      <div className="admin-terms-icon">
                        <i className={item.icon} />
                      </div>
                      <div>
                        <strong>{item.title}</strong>
                        <p>{item.body}</p>
                      </div>
                      <div className="admin-terms-actions">
                        <Button
                          variant="outline-primary"
                          onClick={() => openHighlightEditor(index)}
                          title="Edit ringkasan"
                        >
                          <i className="fas fa-pen" />
                        </Button>
                        <Button
                          variant="outline-danger"
                          onClick={() => deleteContent("highlight", index)}
                          title="Hapus ringkasan"
                        >
                          <i className="fas fa-trash-alt" />
                        </Button>
                      </div>
                    </article>
                  </Col>
                ))}
              </Row>
            )}
          </section>

          <section className="admin-terms-block">
            <div className="admin-terms-block-title">
              <div>
                <h3>Ketentuan Lengkap</h3>
                <span>{terms.sections.length} kategori informasi</span>
              </div>
              <Button variant="success" onClick={() => openSectionEditor()}>
                <i className="fas fa-plus" />
                Tambah kategori
              </Button>
            </div>

            <div className="admin-terms-section-list">
              {terms.sections.length === 0 && (
                <div className="admin-terms-empty">
                  Belum ada kategori ketentuan.
                </div>
              )}
              {terms.sections.map((section, sectionIndex) => (
                <article
                  className="admin-terms-section"
                  key={section._id || `${section.title}-${sectionIndex}`}
                >
                  <div className="admin-terms-section-head">
                    <div className="admin-terms-section-name">
                      <div className="admin-terms-icon">
                        <i className={section.icon} />
                      </div>
                      <div>
                        <h4>{section.title}</h4>
                        <Badge variant="light">
                          {section.items.length} detail
                        </Badge>
                      </div>
                    </div>
                    <div className="admin-terms-section-tools">
                      <Button
                        variant="outline-success"
                        onClick={() => openItemEditor(sectionIndex)}
                      >
                        <i className="fas fa-plus" />
                        Tambah detail
                      </Button>
                      <Button
                        variant="outline-primary"
                        onClick={() => openSectionEditor(sectionIndex)}
                        title="Edit kategori"
                      >
                        <i className="fas fa-pen" />
                      </Button>
                      <Button
                        variant="outline-danger"
                        onClick={() =>
                          deleteContent("section", sectionIndex)
                        }
                        title="Hapus kategori"
                      >
                        <i className="fas fa-trash-alt" />
                      </Button>
                    </div>
                  </div>

                  <div className="admin-terms-item-list">
                    {section.items.length === 0 && (
                      <div className="admin-terms-empty compact">
                        Belum ada detail pada kategori ini.
                      </div>
                    )}
                    {section.items.map((item, itemIndex) => (
                      <div
                        className="admin-terms-item"
                        key={item._id || `${item.title}-${itemIndex}`}
                      >
                        <div className="admin-terms-item-copy">
                          <strong>{item.title}</strong>
                          {Array.isArray(item.body) ? (
                            <ul>
                              {item.body.map((line) => (
                                <li key={line}>{line}</li>
                              ))}
                            </ul>
                          ) : (
                            <p>{item.body}</p>
                          )}
                          {(item.links || []).length > 0 && (
                            <div className="admin-terms-link-badges">
                              {item.links.map((link) => (
                                <span key={link.url}>
                                  <i className="fas fa-link" />
                                  {link.label}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="admin-terms-actions">
                          <Button
                            variant="outline-primary"
                            onClick={() =>
                              openItemEditor(sectionIndex, itemIndex)
                            }
                            title="Edit detail"
                          >
                            <i className="fas fa-pen" />
                          </Button>
                          <Button
                            variant="outline-danger"
                            onClick={() =>
                              deleteContent("item", sectionIndex, itemIndex)
                            }
                            title="Hapus detail"
                          >
                            <i className="fas fa-trash-alt" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </section>
        </>
      )}

      <Modal
        show={editor.show}
        onHide={closeEditor}
        centered
        size={editor.type === "item" ? "lg" : "md"}
        className="admin-terms-modal"
      >
        <Form onSubmit={submitEditor}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editor.type === "intro" && "Edit pembuka"}
              {editor.type === "highlight" &&
                `${editor.index === null ? "Tambah" : "Edit"} ringkasan`}
              {editor.type === "section" &&
                `${editor.sectionIndex === null ? "Tambah" : "Edit"} kategori`}
              {editor.type === "item" &&
                `${editor.itemIndex === null ? "Tambah" : "Edit"} detail ketentuan`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Judul</Form.Label>
              <Form.Control
                value={editor.title}
                onChange={(event) =>
                  updateEditor("title", event.target.value)
                }
                maxLength={180}
                required
                autoFocus
              />
            </Form.Group>

            {editor.type !== "intro" && editor.type !== "item" && (
              <Form.Group>
                <Form.Label>Ikon</Form.Label>
                <Form.Control
                  as="select"
                  value={editor.icon}
                  onChange={(event) =>
                    updateEditor("icon", event.target.value)
                  }
                >
                  {ICON_OPTIONS.map((icon) => (
                    <option key={icon.value} value={icon.value}>
                      {icon.label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            )}

            {editor.type !== "section" && (
              <Form.Group>
                <Form.Label>Isi</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={editor.type === "item" ? 6 : 5}
                  value={editor.body}
                  onChange={(event) =>
                    updateEditor("body", event.target.value)
                  }
                  maxLength={5000}
                  required
                />
              </Form.Group>
            )}

            {editor.type === "item" && (
              <div className="admin-terms-link-editor">
                <div className="admin-terms-link-editor-title">
                  <Form.Label>Tautan</Form.Label>
                  <Button
                    type="button"
                    variant="outline-success"
                    onClick={addLink}
                  >
                    <i className="fas fa-plus" />
                    Tambah tautan
                  </Button>
                </div>
                {editor.links.map((link, index) => (
                  <div className="admin-terms-link-row" key={index}>
                    <Form.Control
                      aria-label="Nama tautan"
                      placeholder="Nama tautan"
                      value={link.label}
                      onChange={(event) =>
                        updateLink(index, "label", event.target.value)
                      }
                      maxLength={120}
                    />
                    <Form.Control
                      type="url"
                      aria-label="URL tautan"
                      placeholder="https://..."
                      value={link.url}
                      onChange={(event) =>
                        updateLink(index, "url", event.target.value)
                      }
                      maxLength={1000}
                    />
                    <Button
                      type="button"
                      variant="outline-danger"
                      onClick={() => removeLink(index)}
                      title="Hapus tautan"
                    >
                      <i className="fas fa-times" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeEditor} disabled={saving}>
              Batal
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? (
                <i className="fas fa-circle-notch fa-spin" />
              ) : (
                <i className="fas fa-save" />
              )}
              Simpan
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}
